import React, { useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import projects from "../../data/projects";

gsap.registerPlugin(ScrollTrigger);

const ProjectDetail = () => {
  const { slug } = useParams();
  const project = projects.find((p) => p.slug === slug);
  const sectionRef = useRef(null);
  const headingRef = useRef(null);
  const contentRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    if (!project) return;

    const ctx = gsap.context(() => {
      // Heading animation
      gsap.from(headingRef.current, {
        y: 80,
        opacity: 0,
        duration: 1,
        delay: 0.3,
        ease: "power3.out",
      });

      // Content sections animation
      const sections = contentRef.current.querySelectorAll(".detail-section");
      sections.forEach((section, index) => {
        gsap.from(section, {
          y: 60,
          opacity: 0,
          duration: 1,
          delay: 0.5 + index * 0.15,
          ease: "power3.out",
          scrollTrigger: {
            trigger: section,
            start: "top 85%",
            toggleActions: "play none none reverse",
          },
        });
      });
    }, sectionRef);

    return () => ctx.revert();
  }, [project]);

  if (!project) {
    return (
      <section className="min-h-screen flex flex-col items-center justify-center bg-[#0a0a0a] px-6">
        <h1 className="text-4xl font-display font-bold mb-6">
          Project Not Found
        </h1>
        <p className="text-[#888] mb-8">
          The project you're looking for doesn't exist.
        </p>
        <Link to="/" className="btn-primary magnetic">
          <span>Back to Home</span>
        </Link>
      </section>
    );
  }

  return (
    <section
      ref={sectionRef}
      className="relative min-h-screen bg-[#0a0a0a] overflow-hidden"
    >
      {/* Background accent */}
      <div className="circle-accent top-[-200px] right-[-200px]" />
      <div className="circle-accent bottom-[-200px] left-[-200px] bg-gradient-to-r from-[#00fff0] to-transparent" />

      <div className="max-w-5xl mx-auto px-6 md:px-12 pt-40 pb-32">
        {/* Back link */}
        <Link
          to="/"
          className="inline-flex items-center gap-3 text-[#888] hover:text-[#c9ff00] transition-colors duration-300 mb-16 group"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className="transform group-hover:-translate-x-1 transition-transform duration-300"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
          <span className="text-sm tracking-wider uppercase">Back to Work</span>
        </Link>

        {/* Header */}
        <div ref={headingRef} className="mb-20">
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <span className="px-4 py-2 text-xs tracking-wider uppercase bg-[#c9ff00] text-[#0a0a0a] rounded-full font-medium">
              {project.category}
            </span>
            {project.year && (
              <span className="px-4 py-2 text-xs tracking-wider uppercase border border-[#333] rounded-full text-[#888]">
                {project.year}
              </span>
            )}
          </div>

          <h1 className="text-5xl md:text-7xl lg:text-8xl font-display font-bold mb-8 leading-none">
            {project.title}
          {/* SEO: heading utama */}
          <meta itemProp="name" content={project.title} />
          </h1>

          {/* Stack */}
          <div className="flex flex-wrap gap-2 mb-10">
            {project.stack.map((tech, i) => (
              <span
                key={i}
                className="px-4 py-2 text-xs tracking-wider uppercase border border-[#333] rounded-full text-[#888]"
              >
                {tech}
              </span>
            ))}
          </div>

          {/* Prototype link */}
          <a
            href={project.link}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary magnetic inline-flex"
          >
            <span>View Prototype</span>
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1 11L11 1M11 1H1M11 1V11"
                stroke="currentColor"
                strokeWidth="1.5"
              />
            </svg>
          </a>
        </div>

        {/* Divider */}
        <div className="divider mb-20" />

        {/* Content sections */}
        <div ref={contentRef} className="space-y-20">
          {/* Background */}
          <div className="detail-section">
            <div className="flex items-center gap-4 mb-6">
              <span className="text-5xl font-display font-bold text-[#222]">
                01
              </span>
              <h2 className="text-sm font-medium tracking-[0.2em] uppercase text-[#888]">
                Background
              </h2>
            </div>
            <p className="text-xl md:text-2xl text-[#ccc] leading-relaxed max-w-3xl">
              {project.background}
            </p>
          </div>

          {/* Problem */}
          <div className="detail-section">
            <div className="flex items-center gap-4 mb-6">
              <span className="text-5xl font-display font-bold text-[#222]">
                02
              </span>
              <h2 className="text-sm font-medium tracking-[0.2em] uppercase text-[#888]">
                Problem
              </h2>
            </div>
            <div className="p-8 md:p-12 border border-[#222] rounded-2xl bg-[#111]">
              <p className="text-lg md:text-xl text-[#aaa] leading-relaxed">
                {project.problem}
              </p>
            </div>
          </div>

          {/* Solution */}
          <div className="detail-section">
            <div className="flex items-center gap-4 mb-6">
              <span className="text-5xl font-display font-bold text-[#222]">
                03
              </span>
              <h2 className="text-sm font-medium tracking-[0.2em] uppercase text-[#888]">
                Solution
              </h2>
            </div>
            <p className="text-xl md:text-2xl text-[#ccc] leading-relaxed max-w-3xl">
              {project.solution}
            </p>
          </div>

          {/* Outcome */}
          <div className="detail-section">
            <div className="flex items-center gap-4 mb-6">
              <span className="text-5xl font-display font-bold text-[#222]">
                04
              </span>
              <h2 className="text-sm font-medium tracking-[0.2em] uppercase text-[#888]">
                Outcome
              </h2>
            </div>
            <div className="p-8 md:p-12 border border-[#c9ff00]/20 rounded-2xl bg-[#c9ff00]/5">
              <p className="text-lg md:text-xl text-[#ccc] leading-relaxed">
                {project.outcome}
              </p>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="divider my-20" />

        {/* Navigation */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-8">
          <Link
            to="/"
            className="btn-outline magnetic"
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
            <span>All Projects</span>
          </Link>

          <a
            href={project.link}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary magnetic"
          >
            <span>View Prototype</span>
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1 11L11 1M11 1H1M11 1V11"
                stroke="currentColor"
                strokeWidth="1.5"
              />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
};

export default ProjectDetail;
